import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { completeAndSignConsultationCompletionDetails } from "~/providers/api/consultationDetails";
import { AssessmentAndPlanData } from "~/providers/utils/types/consultation";
import { useGetPatient } from "../useGetPatient";

const DEFAULT_COMPLETE_AND_SIGN_ERROR = "Unable to process request";
const getCompleteAndSignErrorMessage = (error: unknown) => {
  const detail = (
    error as {
      response?: {
        data?: {
          detail?: unknown;
        };
      };
    }
  )?.response?.data?.detail;
  // Backend may return a plain detail string
  if (typeof detail === "string" && detail.trim().length > 0) {
    return detail;
  }
  // Backend may return field-level errors as an object
  if (detail && typeof detail === "object") {
    const referNoteError = (detail as Record<string, unknown>).refer_note;
    if (typeof referNoteError === "string" && referNoteError.trim().length > 0)
      return referNoteError;
  }
};

export const useConsultationCompleteAndSign = (
  patientId: string,
  onSuccess: () => void,
) => {
  const queryClient = useQueryClient();

  const { data: patientData } = useGetPatient(patientId);
  const consultationId = patientData?.consultation?.id;

  const { mutate: consultationCompleteAndSignMutation, isPending } =
    useMutation({
      mutationFn: (consultationDetails: AssessmentAndPlanData) =>
        completeAndSignConsultationCompletionDetails({
          consultationId,
          consultationDetails,
        }),
      onSuccess: async () => {
        onSuccess();
        await queryClient.invalidateQueries({
          queryKey: ["getPatient", patientId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["getConsultationCompletionDetail", consultationId],
        });
        toast.success("Consultation completed successfully!");
      },
      onError: (error) =>
        toast.error(
          getCompleteAndSignErrorMessage(error) ??
            DEFAULT_COMPLETE_AND_SIGN_ERROR,
        ),
    });

  return { consultationCompleteAndSignMutation, isPending };
};
